import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGuardedCallback } from '../../permissions/useGuardedAction';
import {
  PermissionLevel,
  PermissionResource,
} from '../../permissions/permission-catalog';
import type { useTeamChatSocket } from '../../hooks/useTeamChatSocket';
import { useApi } from '../../state/ApiContext';
import type {
  ApprovalItem,
  CardRef,
  CostPricingState,
  VoiceAgentOption,
  VoiceConversationCostRollup,
  WorkStreamSummary,
} from '../../client';
import type { WikiRefProject } from '../../lib/wiki-refs';
import { ApprovalCard } from '../../approvals/ApprovalCard';
import { useApprovalEvents } from '../../state/ApprovalEventsContext';
import ChatMessageList from './ChatMessageList';
import ChatComposer from './ChatComposer';
import ComposerFundsNotice from './ComposerFundsNotice';
import VoiceDeck from './VoiceDeck';
import { voiceAgentIsAvailable } from './VoiceAgentSelector';
import VoiceAgentPickerModal from './VoiceAgentPickerModal';
import '../../agent-chat.css';
import { sessionFetch } from '../../session-client';
import { useVoiceChannel } from '../../voice/useVoiceChannel';
import type { ProvisionalVoiceTranscript } from '../../voice/voice-types';
import { formatCostTotal } from '../../utils/formatCost';
import type { VoiceCostState } from './voiceCost';

const ProvisionalVoiceTimeline: React.FC<{
  transcripts: Readonly<Record<string, ProvisionalVoiceTranscript>>;
  durableTranscriptSlots: ReadonlySet<string>;
  turnKeys?: ReadonlySet<string>;
}> = ({ transcripts, durableTranscriptSlots, turnKeys }) => {
  const entries = Object.entries(transcripts).flatMap(([turnKey, turn]) =>
    turnKeys && !turnKeys.has(turnKey)
      ? []
      : [turn.input, turn.output]
          .map((text, index) => ({ text: text.trim(), index }))
          .filter(
            ({ text, index }) =>
              text.length > 0 &&
              !durableTranscriptSlots.has(
                `${turnKey}:${index === 0 ? 'input' : 'output'}`,
              ),
          )
          .map(({ text, index }) => ({
            key: `${turnKey}:${index}`,
            text,
            kind: index === 0 ? 'input' : 'output',
            label:
              index === 0 ? 'Voice input' : (turn.agentName ?? 'Voice reply'),
          })),
  );
  if (entries.length === 0) return null;
  return (
    <div className="voice-provisional-timeline" aria-live="polite">
      {entries.map((entry) => (
        <div
          className={`voice-provisional-bubble voice-provisional-bubble-${entry.kind}`}
          key={entry.key}>
          <span>{entry.label}</span>
          <p>{entry.text}</p>
        </div>
      ))}
    </div>
  );
};

export const ContinuationApproval: React.FC<{
  approvalId: number;
}> = ({ approvalId }) => {
  const { approvalsApi } = useApi();
  const { epoch, invalidate } = useApprovalEvents();
  const [item, setItem] = useState<ApprovalItem | null>(null);
  const [deciding, setDeciding] = useState(false);

  useEffect(() => {
    let active = true;
    approvalsApi
      .apiApprovalsApprovalIdGet(approvalId)
      .then((response) => active && setItem(response.data.approval))
      .catch((error) =>
        console.error('Failed to load continuation approval:', error),
      );
    return () => {
      active = false;
    };
  }, [approvalId, approvalsApi, epoch]);

  // Declared BEFORE the early return: a hook after a conditional return is
  // called on only some renders, which React reports as "Rendered more hooks
  // than during the previous render" and unmounts the subtree — here the
  // approval banner, which then never appeared at all.
  const decideGuard = useGuardedCallback<
    [
      decision: 'approved' | 'rejected',
      note: string,
      inputs?: Record<string, unknown>,
    ],
    Promise<void>
  >({
    resource: PermissionResource.APPROVAL,
    level: PermissionLevel.MANAGE,
    verb: 'decide',
    subject: 'pending approvals',
    testId: 'team-chat-approval-decide-denied',
    onAllowed: async (decision, note, inputs) => {
      setDeciding(true);
      try {
        await approvalsApi.apiApprovalsApprovalIdDecisionPost(approvalId, {
          decision,
          note: note || undefined,
          inputs,
        });
        setItem(null);
        invalidate(approvalId);
      } finally {
        setDeciding(false);
      }
    },
  });

  if (!item || item.status !== 'pending') return null;

  return (
    <>
      <ApprovalCard
        approval={item}
        deciding={deciding}
        onDecide={(decision, note, inputs) =>
          // The card awaits this for its own spinner.
          decideGuard.run(decision, note, inputs) ?? Promise.resolve()
        }
      />
      {decideGuard.denialModal}
    </>
  );
};

/** Everything useTeamChatSocket returns; passed through as one bag. */
export type ChatSocket = ReturnType<typeof useTeamChatSocket>;

interface DurableCostSummary {
  totalCostUsd: number | null;
  knownTotalCostUsd: number;
  costPricingState: CostPricingState;
  metricCount: number;
}

function costSummaryText(
  totalUsd: number | null,
  knownTotalUsd: number,
  pricingState: CostPricingState,
): string {
  if (totalUsd !== null) return formatCostTotal(totalUsd);
  if (knownTotalUsd > 0) {
    return `${formatCostTotal(knownTotalUsd)} known + unpriced usage`;
  }
  return pricingState === 'unpriced' || pricingState === 'partially_unpriced'
    ? 'Unpriced usage'
    : formatCostTotal(totalUsd);
}

interface VoiceTeamComposerProps {
  teamId: number;
  conversationId: number;
  voiceAgents: VoiceAgentOption[];
  chat: ChatSocket;
  draft: string;
  onDraftChange: (value: string) => void;
  composer: React.ComponentProps<typeof ChatComposer>;
  onDurableCostRefreshSignal: () => void;
  voiceCost?: VoiceConversationCostRollup | null;
  voiceCostState: VoiceCostState;
  initialAgentId?: number;
  onProvisionalTranscriptsChange: (
    transcripts: Readonly<Record<string, ProvisionalVoiceTranscript>>,
  ) => void;
}

const VoiceTeamComposer: React.FC<VoiceTeamComposerProps> = ({
  teamId,
  conversationId,
  voiceAgents,
  chat,
  draft,
  onDraftChange,
  composer,
  onDurableCostRefreshSignal,
  voiceCost,
  voiceCostState,
  initialAgentId,
  onProvisionalTranscriptsChange,
}) => {
  const voice = useVoiceChannel(conversationId);
  const eligibleAgents = voiceAgents.filter(voiceAgentIsAvailable);
  const [deckOpen, setDeckOpen] = useState(initialAgentId !== undefined);
  const [pickerOpen, setPickerOpen] = useState(false);
  const restoreFocusRef = useRef(false);
  const composerRootRef = useRef<HTMLDivElement>(null);
  const ownsVoiceChannelRef = useRef(false);
  const endVoiceRef = useRef(voice.end);
  const durableCostRefreshSignatureRef = useRef<string>();
  const initialOpenStartedRef = useRef(false);

  useEffect(() => {
    if (initialAgentId === undefined || initialOpenStartedRef.current) return;
    initialOpenStartedRef.current = true;
    ownsVoiceChannelRef.current = true;
    void voice.open({ teamId, agentId: initialAgentId }).catch(() => undefined);
  }, [initialAgentId, teamId, voice.open]);

  useEffect(() => {
    endVoiceRef.current = voice.end;
  }, [voice.end]);

  useEffect(() => {
    onProvisionalTranscriptsChange(voice.provisionalTranscripts);
  }, [onProvisionalTranscriptsChange, voice.provisionalTranscripts]);

  useEffect(() => {
    const signature = `${voice.session?.sessionId ?? ''}:${
      voice.session?.leaseVersion ?? ''
    }:${voice.status}:${voice.providerUsageObservationGeneration}`;
    if (durableCostRefreshSignatureRef.current === undefined) {
      durableCostRefreshSignatureRef.current = signature;
      return;
    }
    if (durableCostRefreshSignatureRef.current !== signature) {
      durableCostRefreshSignatureRef.current = signature;
      onDurableCostRefreshSignal();
    }
  }, [
    onDurableCostRefreshSignal,
    voice.providerUsageObservationGeneration,
    voice.session?.leaseVersion,
    voice.session?.sessionId,
    voice.status,
  ]);

  useEffect(
    () => () => {
      if (ownsVoiceChannelRef.current) {
        void endVoiceRef.current().catch(() => undefined);
      }
      onProvisionalTranscriptsChange({});
    },
    [onProvisionalTranscriptsChange],
  );

  useEffect(() => {
    if (deckOpen || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    composerRootRef.current
      ?.querySelector<HTMLTextAreaElement>('.chat-composer-input')
      ?.focus();
  }, [deckOpen]);

  const closeDeck = (): void => {
    ownsVoiceChannelRef.current = false;
    restoreFocusRef.current = true;
    setDeckOpen(false);
  };

  const startVoice = (): void => {
    const activeAgent = voice.isCurrentConversation
      ? eligibleAgents.find(
          (agent) => agent.agentId === voice.session?.currentAgentId,
        )
      : undefined;
    const agent =
      activeAgent ??
      (eligibleAgents.length === 1 ? eligibleAgents[0] : undefined);
    if (!agent) {
      setPickerOpen(true);
      return;
    }
    ownsVoiceChannelRef.current = true;
    setDeckOpen(true);
    void voice.open({ teamId, agentId: agent.agentId }).catch(() => undefined);
  };

  const chooseAgent = (agentId: number): void => {
    setPickerOpen(false);
    ownsVoiceChannelRef.current = true;
    setDeckOpen(true);
    void voice.open({ teamId, agentId }).catch(() => undefined);
  };

  return (
    <div className="voice-team-composer" ref={composerRootRef}>
      <div hidden={deckOpen}>
        <ChatComposer
          {...composer}
          value={draft}
          onValueChange={onDraftChange}
          onVoiceStart={startVoice}
        />
      </div>
      {deckOpen && (
        <VoiceDeck
          teamId={teamId}
          conversationId={conversationId}
          agents={eligibleAgents}
          isProcessing={chat.isProcessing}
          approvalPending={Boolean(chat.approval)}
          onEnd={closeDeck}
          voiceCost={voiceCost}
          voiceCostState={voiceCostState}
        />
      )}
      <VoiceAgentPickerModal
        isOpen={pickerOpen}
        agents={eligibleAgents}
        currentAgentId={voice.session?.currentAgentId}
        onClose={() => setPickerOpen(false)}
        onSelect={chooseAgent}
      />
    </div>
  );
};

const DurableVoiceUsageObserver: React.FC<{
  conversationId: number;
  onDurableCostRefreshSignal: () => void;
}> = ({ conversationId, onDurableCostRefreshSignal }) => {
  const voice = useVoiceChannel(conversationId);
  const signatureRef = useRef<string>();
  const signature = [
    voice.session?.sessionId ?? '',
    voice.session?.leaseVersion ?? '',
    voice.status,
    voice.providerUsageObservationGeneration,
  ].join(':');

  useEffect(() => {
    if (signatureRef.current === undefined) {
      signatureRef.current = signature;
      return;
    }
    if (signatureRef.current !== signature) {
      signatureRef.current = signature;
      onDurableCostRefreshSignal();
    }
  }, [onDurableCostRefreshSignal, signature]);

  return null;
};

function agentIsUsable(scope: VoiceAgentOption[], agentId: number): boolean {
  if (scope.length === 0) return true;
  const agent = scope.find((candidate) => candidate.agentId === agentId);
  return agent !== undefined && voiceAgentIsAvailable(agent);
}

const NO_VOICE_AGENTS_STATUS = 'No voice agents are currently available.';

/**
 * The preflight status for a scope that offers nothing to start. A scope of
 * exactly one agent can say why that agent is out; anything else can only say
 * that nothing is available.
 */
function unavailableScopeStatus(agents: VoiceAgentOption[]): string {
  const only = agents.length === 1 ? agents[0] : undefined;
  return only?.unavailableReason || NO_VOICE_AGENTS_STATUS;
}

interface VoiceActivationComposerProps
  extends Omit<VoiceTeamComposerProps, 'conversationId' | 'initialAgentId'> {}

const VoiceActivationComposer: React.FC<VoiceActivationComposerProps> = ({
  teamId,
  voiceAgents,
  chat,
  draft,
  onDraftChange,
  composer,
  onDurableCostRefreshSignal,
  voiceCost,
  voiceCostState,
  onProvisionalTranscriptsChange,
}) => {
  const availableAgents = voiceAgents.filter(voiceAgentIsAvailable);
  const voiceAgentsRef = useRef(voiceAgents);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [preflightStatus, setPreflightStatus] = useState<string | null>(null);
  const [activation, setActivation] = useState<{
    conversationId: number;
    agentId: number;
  } | null>(null);

  useEffect(() => {
    voiceAgentsRef.current = voiceAgents;
  }, [voiceAgents]);

  const activate = React.useCallback(
    (agentId: number): void => {
      /*
       * Reject only on positive evidence that this agent cannot be used. The
       * scope arrives asynchronously and is empty while it settles - TeamChat
       * clears it whenever the options refetch, and re-derives it from the
       * conversation's own messages - so an empty list means "not known yet",
       * not "gone". Treating those the same abandoned the activation with a
       * bare return and left the deck unmounted for good, because nothing
       * retries a start the user only asks for once.
       */
      if (!agentIsUsable(voiceAgentsRef.current, agentId)) return;
      setPreflightStatus('Starting voice chat…');
      void chat
        .ensureConversationId()
        .then((durableConversationId) => {
          // Same rule once the conversation id resolves: a scope that has
          // since narrowed to a different set does revoke this agent, but a
          // scope that has not settled yet does not.
          if (!agentIsUsable(voiceAgentsRef.current, agentId)) {
            const currentAvailable = voiceAgentsRef.current.filter(
              voiceAgentIsAvailable,
            );
            setPreflightOpen(true);
            setPreflightStatus(
              currentAvailable.length === 0
                ? 'No voice agents are currently available.'
                : 'Choose an agent',
            );
            return;
          }
          setActivation({
            conversationId: durableConversationId,
            agentId,
          });
          setPreflightOpen(false);
          setPreflightStatus(null);
        })
        .catch((error: unknown) => {
          setPreflightStatus(
            error instanceof Error
              ? error.message
              : 'Could not start voice chat',
          );
        });
    },
    [chat],
  );

  useEffect(() => {
    if (
      activation &&
      chat.conversationId != null &&
      chat.conversationId !== activation.conversationId
    ) {
      setActivation(null);
      setPreflightOpen(false);
      setPreflightStatus(null);
    }
  }, [activation, chat.conversationId]);

  // The start button is live before the voice options finish loading, so a
  // preflight opened early holds the empty-scope message. Once the options
  // land, reconcile it against the scope we actually got — including a scope
  // whose single agent is unavailable, which owes the reader its reason.
  useEffect(() => {
    if (
      !preflightOpen ||
      activation !== null ||
      preflightStatus !== NO_VOICE_AGENTS_STATUS
    ) {
      return;
    }
    if (availableAgents.length === 1) {
      activate(availableAgents[0].agentId);
    } else if (availableAgents.length > 1) {
      setPreflightStatus(null);
    } else {
      setPreflightStatus(unavailableScopeStatus(voiceAgents));
    }
  }, [
    activate,
    activation,
    availableAgents,
    preflightOpen,
    preflightStatus,
    voiceAgents,
  ]);

  if (activation) {
    return (
      <VoiceTeamComposer
        key={`${activation.conversationId}:${activation.agentId}`}
        teamId={teamId}
        conversationId={activation.conversationId}
        voiceAgents={availableAgents}
        chat={chat}
        draft={draft}
        onDraftChange={onDraftChange}
        composer={composer}
        onDurableCostRefreshSignal={onDurableCostRefreshSignal}
        voiceCost={voiceCost}
        voiceCostState={voiceCostState}
        initialAgentId={activation.agentId}
        onProvisionalTranscriptsChange={onProvisionalTranscriptsChange}
      />
    );
  }

  const startVoice = (): void => {
    setPreflightStatus(null);
    if (availableAgents.length === 1) {
      activate(availableAgents[0].agentId);
      return;
    }
    setPreflightOpen(true);
    if (availableAgents.length === 0) {
      setPreflightStatus(unavailableScopeStatus(voiceAgents));
    }
  };

  return (
    <div className="voice-team-composer">
      {chat.conversationId != null && (
        <DurableVoiceUsageObserver
          conversationId={chat.conversationId}
          onDurableCostRefreshSignal={onDurableCostRefreshSignal}
        />
      )}
      <ChatComposer
        {...composer}
        value={draft}
        onValueChange={onDraftChange}
        onVoiceStart={startVoice}
      />
      <VoiceAgentPickerModal
        isOpen={preflightOpen}
        agents={availableAgents}
        busy={preflightStatus === 'Starting voice chat…'}
        status={preflightStatus}
        onClose={() => {
          setPreflightOpen(false);
          setPreflightStatus(null);
        }}
        onSelect={activate}
      />
    </div>
  );
};

interface ChatSurfaceProps {
  teamId: string;
  chat: ChatSocket;
  /** Extra classes on the .agent-chat container (e.g. form-assist-chat) */
  className?: string;
  /** Project card references for #KEY-N autocomplete + display expansion */
  cardRefs?: CardRef[];
  /** Projects for wiki:KEY/page ref rendering */
  projects?: WikiRefProject[];
  /** Whether agent names render as links / are mentionable */
  agentsEnabled?: boolean;
  /** Extra mentionables offered by the composer (team users) */
  mentionables?: { id: number; name: string; unavailable?: boolean }[];
  /** Idle composer placeholder */
  composerPlaceholder?: string;
  /** One-shot programmatic composer draft */
  composerSeed?: { text: string; nonce: number } | null;
  /** Focus request retained across plain/voice composer remounts. */
  composerFocusNonce?: number | null;
  /** Agent selected by an external launcher for the first message only. */
  initialTargetAgent?: { id: number; name: string } | null;
  /** Called after the first targeted message is accepted for sending. */
  onInitialTargetUsed?: () => void;
  voiceAgents?: VoiceAgentOption[];
  conversationCost?: DurableCostSummary;
  voiceCost?: VoiceConversationCostRollup | null;
  voiceCostState?: VoiceCostState;
  onDurableCostRefreshSignal?: () => void;
  /**
   * Applied to every outgoing message (user sends AND interaction
   * responses) before it goes to the socket — the form-assist surface
   * appends its machine-readable form state here.
   */
  transformOutgoing?: (text: string) => string;
  /** Container testid (defaults to none; TeamChat has its own wrapper) */
  testId?: string;
}

/**
 * The chat experience itself — transcript, approval banners, funds
 * notice, latest work-stream link, composer — shared VERBATIM by the
 * team chat dock and the form-assist modal pane, so the two are visually
 * and functionally identical. Owns nothing about conversation selection:
 * headers/pickers belong to the caller.
 */
const ChatSurface: React.FC<ChatSurfaceProps> = ({
  teamId,
  chat,
  className,
  cardRefs,
  projects,
  agentsEnabled = true,
  mentionables = [],
  composerPlaceholder,
  composerSeed = null,
  composerFocusNonce = null,
  initialTargetAgent = null,
  onInitialTargetUsed,
  voiceAgents,
  conversationCost,
  voiceCost,
  voiceCostState = 'loading',
  onDurableCostRefreshSignal = () => undefined,
  transformOutgoing,
  testId,
}) => {
  const { workStreamsApi, chatApi } = useApi();
  const [latestWorkStream, setLatestWorkStream] =
    useState<WorkStreamSummary | null>(null);
  const [composerDraft, setComposerDraft] = useState('');
  // The composer subtree remounts on its own (a new conversation generation,
  // and the voice branch appearing once the team's agents are known), which
  // resets the composer's own applied-seed guard. Holding the applied nonce
  // out here — above every remount — keeps a one-shot seed one-shot, instead
  // of replaying it over a draft that has been typed into since.
  const appliedComposerSeedNonce = useRef<number | null>(null);
  const [provisionalVoiceTranscripts, setProvisionalVoiceTranscripts] =
    useState<Readonly<Record<string, ProvisionalVoiceTranscript>>>({});
  const [compactNotice, setCompactNotice] = useState<string | null>(null);
  const compactNoticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showCompactNotice = (notice: string | null, transient = true) => {
    if (compactNoticeTimer.current) clearTimeout(compactNoticeTimer.current);
    setCompactNotice(notice);
    if (notice && transient) {
      compactNoticeTimer.current = setTimeout(
        () => setCompactNotice(null),
        6_000,
      );
    }
  };

  /**
   * /compact — the first composer command. Intercepted here, never sent
   * to the agent (matching how OpenAI and Anthropic keep compaction out
   * of the model's hands). Success feedback is the compaction divider the
   * websocket paints; the notice covers only the silent outcomes.
   */
  const runCompactCommand = () => {
    if (!chat.conversationId) {
      showCompactNotice('Nothing to compact yet.');
      return;
    }
    showCompactNotice('Compacting…', false);
    chatApi
      .forceCompactChatConversation(parseInt(teamId, 10), chat.conversationId)
      .then((response) => {
        const results = response.data.results ?? [];
        const compacted = results.some((result) => result.compacted);
        showCompactNotice(compacted ? null : 'Nothing to compact yet.');
      })
      .catch((error) => {
        console.error('Compaction failed:', error);
        showCompactNotice('Compaction failed.');
      });
  };

  const send = (text: string) => {
    if (text.trim().toLowerCase() === '/compact') {
      runCompactCommand();
      return true;
    }
    const sent = chat.sendMessage(
      transformOutgoing ? transformOutgoing(text) : text,
      initialTargetAgent
        ? { targetAgentId: initialTargetAgent.id }
        : undefined,
    );
    if (sent && initialTargetAgent) onInitialTargetUsed?.();
    return sent;
  };

  const latestUserMessageId =
    [...chat.items]
      .reverse()
      .find((item) => item.kind === 'user' && item.messageId != null)
      ?.messageId ?? null;

  const conversationId = chat.conversationId;
  const isProcessing = chat.isProcessing;
  const composer = {
    onSend: send,
    disabled: chat.connectionState !== 'connected',
    isProcessing: chat.isProcessing,
    pendingAttachments: chat.pendingAttachments,
    onAttach: chat.uploadAttachment,
    onRemoveAttachment: chat.removeAttachment,
    onStop: chat.stopProcessing,
    agents: [...(agentsEnabled ? chat.agents : []), ...mentionables],
    cardRefs,
    placeholder: composerPlaceholder,
    seed:
      composerSeed && appliedComposerSeedNonce.current !== composerSeed.nonce
        ? composerSeed
        : null,
    onSeedApplied: (nonce: number) => {
      appliedComposerSeedNonce.current = nonce;
    },
    focusNonce: composerFocusNonce,
    slashCommands: [
      {
        command: '/compact',
        description: 'Summarize older messages to free context',
      },
    ],
  };
  const durableVoiceTranscriptSlots = new Set(
    chat.items.flatMap((item) => {
      if (!item.voiceTurnKey) return [];
      if (item.kind === 'user') return [`${item.voiceTurnKey}:input`];
      if (
        item.kind === 'agent' &&
        item.assistantMessageId != null &&
        (item.status === 'complete' || item.status === 'error')
      ) {
        return [`${item.voiceTurnKey}:output`];
      }
      return [];
    }),
  );
  const provisionalVoiceTurnAnchors = new Map<string, string>();
  chat.items.forEach((item) => {
    if (
      item.kind === 'agent' &&
      item.voiceTurnKey &&
      !provisionalVoiceTurnAnchors.has(item.voiceTurnKey)
    ) {
      provisionalVoiceTurnAnchors.set(item.voiceTurnKey, item.key);
    }
  });
  const anchoredProvisionalVoiceTurnKeys = new Set(
    provisionalVoiceTurnAnchors.keys(),
  );
  const trailingProvisionalVoiceTurnKeys = new Set(
    Object.keys(provisionalVoiceTranscripts).filter(
      (turnKey) => !anchoredProvisionalVoiceTurnKeys.has(turnKey),
    ),
  );

  useEffect(() => {
    if (!conversationId) {
      setLatestWorkStream(null);
      return;
    }
    let active = true;
    let terminalPoll: ReturnType<typeof setTimeout> | undefined;
    const loadLatestWorkStream = async () => {
      try {
        const response =
          await workStreamsApi.getLatestTeamChatConversationWorkStream(
            parseInt(teamId, 10),
            conversationId,
          );
        if (!active) return;
        const workStream = response.data.workStream ?? null;
        setLatestWorkStream(workStream);
        if (workStream?.status === 'active') {
          terminalPoll = setTimeout(loadLatestWorkStream, 1_000);
        }
      } catch (error) {
        if (!active) return;
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (status === 401 || status === 403 || status === 404) {
          setLatestWorkStream(null);
          return;
        }
        // A transient network failure must not strand an active badge forever.
        // Keep the last known state visible and retry at a quieter cadence.
        terminalPoll = setTimeout(loadLatestWorkStream, 5_000);
      }
    };
    void loadLatestWorkStream();
    return () => {
      active = false;
      if (terminalPoll) clearTimeout(terminalPoll);
    };
  }, [
    conversationId,
    isProcessing,
    latestUserMessageId,
    teamId,
    workStreamsApi,
  ]);

  return (
    <div
      className={['chat-surface', className].filter(Boolean).join(' ')}
      data-testid={testId}>
      {chat.connectionState !== 'connected' && (
        <div className="chat-connection-banner">
          {chat.connectionState === 'connecting' && 'Connecting…'}
          {chat.connectionState === 'reconnecting' &&
            'Connection lost. Reconnecting…'}
          {chat.connectionState === 'disconnected' && 'Disconnected.'}
        </div>
      )}

      {chat.globalError && (
        <div className="chat-error-banner" onClick={chat.clearGlobalError}>
          {chat.globalError}
        </div>
      )}

      <ChatMessageList
        /*
         * The work-stream pointer is a standing fact about this conversation,
         * not a control on the composer — it rides at the end of the
         * transcript and scrolls with it, rather than floating in the gap
         * above the input.
         */
        footer={
          latestWorkStream ||
          conversationCost !== undefined ||
          Object.keys(provisionalVoiceTranscripts).length > 0 ? (
            <>
              <ProvisionalVoiceTimeline
                transcripts={provisionalVoiceTranscripts}
                durableTranscriptSlots={durableVoiceTranscriptSlots}
                turnKeys={trailingProvisionalVoiceTurnKeys}
              />
              {latestWorkStream && (
                <div
                  className="chat-work-stream-link"
                  data-testid="chat-work-stream-row">
                  <Link
                    to={`/console/team/${teamId}/work-streams/${latestWorkStream.id}`}
                    title={`Open ${latestWorkStream.agentName || 'agent'} work details`}>
                    View full Work stream
                  </Link>
                  <span>{latestWorkStream.status}</span>
                </div>
              )}
              {conversationCost !== undefined && (
                <div
                  className="chat-conversation-cost"
                  role="note"
                  aria-label={
                    conversationCost.totalCostUsd === null
                      ? 'Total cost; some usage is unpriced'
                      : 'Total cost'
                  }>
                  Total cost ·{' '}
                  {costSummaryText(
                    conversationCost.totalCostUsd,
                    conversationCost.knownTotalCostUsd,
                    conversationCost.costPricingState,
                  )}
                </div>
              )}
            </>
          ) : null
        }
        projects={projects}
        items={chat.items}
        beforeItem={(item) => {
          if (
            !item.voiceTurnKey ||
            provisionalVoiceTurnAnchors.get(item.voiceTurnKey) !== item.key
          ) {
            return null;
          }
          return (
            <ProvisionalVoiceTimeline
              transcripts={provisionalVoiceTranscripts}
              durableTranscriptSlots={durableVoiceTranscriptSlots}
              turnKeys={new Set([item.voiceTurnKey])}
            />
          );
        }}
        cardRefs={cardRefs}
        agents={chat.agents}
        agentsEnabled={agentsEnabled}
        targetAgentName={initialTargetAgent?.name}
        teamId={teamId}
        conversationId={chat.conversationId ?? undefined}
        onRespond={send}
        onResolveInteraction={(messageId, status) => {
          if (!chat.conversationId) return;
          // Best-effort: the prompt already resolved locally
          sessionFetch(
            `/api/teams/${teamId}/chat-conversations/${chat.conversationId}/messages/${messageId}/interaction-resolution`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ status }),
            },
          ).catch((err) => console.error('Error resolving interaction:', err));
        }}
        voiceCost={voiceCost}
        voiceCostState={voiceCostState}
      />

      {chat.approval?.approvalId ? (
        <div
          className="chat-approval-inline"
          data-testid="chat-approval-banner">
          <ContinuationApproval approvalId={chat.approval.approvalId} />
        </div>
      ) : chat.approval ? (
        <div
          className="chat-approval-banner"
          data-testid="chat-approval-banner">
          <span>
            Agents have exchanged {chat.approval.exchangeCount} messages
            {chat.approval.pendingAgentNames.length > 0
              ? ` — ${chat.approval.pendingAgentNames.join(', ')} waiting to continue`
              : ''}
            .
          </span>
          <span>Review this request from the team's pending approvals.</span>
        </div>
      ) : null}

      <ComposerFundsNotice teamId={teamId} />

      {compactNotice && (
        <div
          className="chat-compact-notice"
          data-testid="chat-compact-notice"
          onClick={() => showCompactNotice(null)}>
          {compactNotice}
        </div>
      )}

      {agentsEnabled && voiceAgents !== undefined ? (
        <VoiceActivationComposer
          key={chat.conversationGeneration}
          teamId={parseInt(teamId, 10)}
          voiceAgents={voiceAgents}
          chat={chat}
          draft={composerDraft}
          onDraftChange={setComposerDraft}
          onDurableCostRefreshSignal={onDurableCostRefreshSignal}
          voiceCost={voiceCost}
          voiceCostState={voiceCostState}
          composer={composer}
          onProvisionalTranscriptsChange={setProvisionalVoiceTranscripts}
        />
      ) : (
        <ChatComposer
          {...composer}
          value={composerDraft}
          onValueChange={setComposerDraft}
        />
      )}
    </div>
  );
};

export default ChatSurface;
